import { useEffect, useMemo, useState } from 'react';

export type DebouncedEmitter<T> = {
  schedule(value: T): void;
  cancel(): void;
};

export function createDebouncedEmitter<T>(
  delayMs: number,
  callback: (value: T) => void,
): DebouncedEmitter<T> {
  let timer: ReturnType<typeof setTimeout> | null = null;

  return {
    schedule(value: T) {
      if (timer) {
        clearTimeout(timer);
      }

      timer = setTimeout(() => {
        callback(value);
      }, delayMs);
    },
    cancel() {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
    },
  };
}

export function useDebouncedValue<T>(value: T, delayMs: number = 300): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  const debouncer = useMemo(
    () => createDebouncedEmitter<T>(delayMs, setDebouncedValue),
    [delayMs],
  );

  useEffect(() => {
    debouncer.schedule(value);
    return () => {
      debouncer.cancel();
    };
  }, [value, debouncer]);

  return debouncedValue;
}
